import { Checkbox, DatePicker } from "@racoon/ui";
import { Namespace, useT } from "@racoon/i18n";
import type { ServiceDayFilter } from "../model/use-service-day-filter";

type Props = {
  serviceDay: ServiceDayFilter;
};

/**
 * The single-day filter of a group: a checkbox and a day stepper in one grey
 * pill. The day controls stay visible but inert until the checkbox turns the
 * filter on. Figma frame "Pickup location group / single day".
 *
 * `cn` in @racoon/ui only joins classes, so a class the components set
 * themselves (border, corner, padding, text size) wins by stylesheet order;
 * those overrides carry `!`.
 */
export const ServiceDayFilterControls = ({ serviceDay }: Props) => {
  const t = useT();

  return (
    <div className="flex items-center gap-2 rounded-xl bg-slate-100 p-2">
      {/* No visible label in the frame: the text is the checkbox's name. */}
      <Checkbox
        aria-label={t(Namespace.PickupLocation, "groups.serviceDayFilter")}
        checked={serviceDay.enabled}
        onCheckedChange={(checked) => serviceDay.setEnabled(checked === true)}
        className="h-5 w-5 !rounded-md !border-border-primary bg-white data-[state=checked]:!border-primary"
      />
      <DatePicker
        showDayNav
        value={serviceDay.date}
        onChange={(date) => date && serviceDay.setDate(date)}
        minDate={serviceDay.minDate}
        disabled={!serviceDay.enabled}
        loading={serviceDay.loading}
        navWrapperClassName="flex items-center gap-2"
        // 20px icons as drawn, secondary grey only while the filter is off; the
        // picker sets 16px on its own SVGs, hence the descendant override.
        navButtonClassName="!p-0 h-8 w-8 shrink-0 [&_svg]:size-5 [&_svg]:disabled:text-icon-secondary"
        className="h-8 gap-2 !px-3 !text-sm [&_svg]:size-5 [&_svg]:disabled:text-icon-secondary"
      />
      {serviceDay.failed && (
        <span className="text-sm text-text-error">
          {t(Namespace.PickupLocation, "groups.serviceDayFailed")}
        </span>
      )}
    </div>
  );
};
